import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Button, Stack, TextField, FormControl,
    FormHelperText, InputLabel, Select, MenuItem, Paper, Snackbar,
    Alert, LinearProgress
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { getData, putData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

const mockBanks = ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank', 'Yes Bank', 'Other'];
const accountTypes = ['Salary', 'Savings', 'Current', 'NRE/NRO'];

const defaultFormData = {
    bankName: '',
    otherBankName: '',
    branchName: '',
    accountHolderName: '',
    accountNumber: '',
    accountType: 'Salary',
    ifscCode: '',
    panNumber: '',
    aadhaarNumber: '',
    uanNumber: '',
    esicNumber: '',
    esiEnrolledDate: '',
    pfStartDate: '',
    pfNumber: '',
    gratuityNumber: '',
    passportNumber: '',
    drivingLicenseNumber: '',
    insurancePolicyNumber: '',
    insurancePolicyIssuedDate: '',
};

const formatDateForInput = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
};

function BankingStatutoryDetails({ initialData, navigationContext, onSaveAndReturn, onUpdateEmployeeSuccess, onFormCompletionChange }) {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");

    const [formData, setFormData] = useState(defaultFormData);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [completion, setCompletion] = useState(0);

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Mapping API data to Form State
    const mapInitialDataToForm = useCallback((apiData) => {
        if (!apiData) return defaultFormData;
        const bankVal = apiData.bank_name;
        const isMockBank = mockBanks.includes(bankVal);

        return {
            bankName: isMockBank ? bankVal : (bankVal ? 'Other' : ''),
            otherBankName: !isMockBank && bankVal ? bankVal : '',
            branchName: apiData.branch_name || '',
            accountHolderName: apiData.account_holder_name || '',
            accountNumber: apiData.account_number || '',
            accountType: apiData.account_type || 'Salary',
            ifscCode: apiData.ifsc_code || '',
            panNumber: apiData.pan_number || '',
            aadhaarNumber: apiData.aadhaar_number || '',
            uanNumber: apiData.uan_number || '',
            esicNumber: apiData.esic_number || '',
            esiEnrolledDate: formatDateForInput(apiData.esic_enrollment_date),
            pfStartDate: formatDateForInput(apiData.pf_enrollment_date),
            pfNumber: apiData.pf_number || '',
            gratuityNumber: apiData.gratuity_number || '',
            passportNumber: apiData.passport_number || '',
            drivingLicenseNumber: apiData.driving_license_number || '',
            insurancePolicyNumber: apiData.insurance_policy_number || '',
            insurancePolicyIssuedDate: formatDateForInput(apiData.policy_issued_date),
        };
    }, []);

    const fetchBankingStatutoryDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        setLoading(true);
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${empIdFromUrl}`);
            if (response?.status === 'SUCCESS') {
                const user = response.message.user;
                const merged = { ...(user?.banking_details || {}), ...(user?.statutory_details || {}) };
                setFormData(mapInitialDataToForm(merged));
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to fetch banking & statutory details.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [empIdFromUrl, mapInitialDataToForm]);

    // Load details on mount; fall back to an initialData prop if one is ever passed in
    useEffect(() => {
        if (initialData) {
            setFormData(mapInitialDataToForm(initialData));
        } else {
            fetchBankingStatutoryDetails();
        }
    }, [initialData, mapInitialDataToForm, fetchBankingStatutoryDetails]);

    // Progress Calculation Logic
    const calculateProgress = useCallback(() => {
        const requiredAndOptionalFields = [
            'accountHolderName', 'accountNumber', 'bankName', 'branchName',
            'accountType', 'ifscCode', 'panNumber', 'aadhaarNumber',
            'uanNumber', 'esicNumber', 'esiEnrolledDate', 'pfStartDate',
            'pfNumber', 'gratuityNumber', 'passportNumber', 'drivingLicenseNumber',
            'insurancePolicyNumber', 'insurancePolicyIssuedDate'
        ];

        if (formData.bankName === 'Other') {
            requiredAndOptionalFields.push('otherBankName');
        }

        const filledCount = requiredAndOptionalFields.filter(field => {
            const val = formData[field];
            return val !== '' && val !== null && val !== undefined;
        }).length;

        const percent = Math.round((filledCount / requiredAndOptionalFields.length) * 100);
        setCompletion(percent);

        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percent);
        }
    }, [formData, onFormCompletionChange]);

    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === 'panNumber' || name === 'ifscCode') {
            formattedValue = value.toUpperCase();
        }
        if (['accountNumber', 'aadhaarNumber'].includes(name)) {
            formattedValue = value.replace(/\D/g, '');
        }
        if (['uanNumber', 'esicNumber'].includes(name)) {
            formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }

        setFormData(prev => ({ ...prev, [name]: formattedValue }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleBankSelectChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            bankName: value,
            otherBankName: value !== 'Other' ? '' : prev.otherBankName
        }));
        setValidationErrors(prev => ({ ...prev, bankName: '', otherBankName: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.accountHolderName.trim()) errors.accountHolderName = 'Account holder name is required.';

        if (!formData.accountNumber.trim()) {
            errors.accountNumber = 'Account number is required.';
        } else if (formData.accountNumber.length < 9 || formData.accountNumber.length > 18) {
            errors.accountNumber = 'Enter a valid account number (9-18 digits).';
        }

        if (!formData.bankName) errors.bankName = 'Bank name is required.';
        if (formData.bankName === 'Other' && !formData.otherBankName.trim()) {
            errors.otherBankName = 'Please enter the bank name.';
        }

        if (!formData.ifscCode.trim()) {
            errors.ifscCode = 'IFSC code is required.';
        } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
            errors.ifscCode = 'Enter a valid 11-digit IFSC code.';
        }

        if (!formData.panNumber.trim()) {
            errors.panNumber = 'PAN number is required.';
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.panNumber)) {
            errors.panNumber = 'Enter a valid PAN (e.g., ABCDE1234F).';
        }

        if (!formData.aadhaarNumber.trim()) {
            errors.aadhaarNumber = 'Aadhaar number is required.';
        } else if (!/^\d{12}$/.test(formData.aadhaarNumber)) {
            errors.aadhaarNumber = 'Aadhaar number must be 12 digits.';
        }

        // Conditional Logic Validations
        if (formData.esiEnrolledDate && !formData.esicNumber.trim()) {
            errors.esicNumber = 'ESIC number is required with enrolment date.';
        }
        if (formData.insurancePolicyIssuedDate && !formData.insurancePolicyNumber.trim()) {
            errors.insurancePolicyNumber = 'Policy number is required with issued date.';
        }

        setValidationErrors(errors);
        return errors;
    };

    const handleSaveChanges = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setSnackbar({ open: true, message: 'Please correct the highlighted fields.', severity: 'error' });
            return false;
        }

        setLoading(true);
        const apiPayload = {
            user_id: empIdFromUrl,
            bankingDetails: {
                account_holder_name: formData.accountHolderName.trim(),
                account_number: formData.accountNumber.trim(),
                bank_name: formData.bankName === 'Other' ? formData.otherBankName.trim() : formData.bankName,
                branch_name: formData.branchName.trim(),
                ifsc_code: formData.ifscCode.trim(),
                account_type: formData.accountType
            },
            statutoryDetails: {
                pan_number: formData.panNumber.trim(),
                aadhaar_number: formData.aadhaarNumber.trim(),
                uan_number: formData.uanNumber.trim(),
                esic_number: formData.esicNumber.trim() || null,
                esic_enrollment_date: formData.esiEnrolledDate || null,
                pf_number: formData.pfNumber.trim() || null,
                pf_enrollment_date: formData.pfStartDate || null,
                gratuity_number: formData.gratuityNumber.trim() || null,
                passport_number: formData.passportNumber.trim() || null,
                driving_license_number: formData.driving_license_number || null,
                insurance_policy_number: formData.insurancePolicyNumber.trim() || null,
                policy_issued_date: formData.insurancePolicyIssuedDate || null,
            }
        };

        try {
            const response = await putData(API_ENDPOINTS.updateEmployeeFinanceAndStatutory, apiPayload);
            if (response.status === 'SUCCESS') {
                setSnackbar({ open: true, message: 'Details Saved Successfully!', severity: 'success' });
                await fetchBankingStatutoryDetails();
                onUpdateEmployeeSuccess?.();
                if (navigationContext?.returnToRehire && onSaveAndReturn) {
                    onSaveAndReturn();
                }
                return true;
            }
            throw new Error(response?.message || 'Update failed');
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error.message || 'Server Error';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            return false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', mb: 3, pb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>Banking Details</Typography>
                </Box>
                <Box sx={{ width: { xs: 110, sm: 220 } }}>
                    <Typography variant="body2" fontWeight={600} textAlign="right" sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                        {`Form Completion: ${completion}%`}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={completion}
                        sx={{ height: 8, borderRadius: 5, mt: 0.5 }}
                        color={completion === 100 ? 'success' : 'primary'}
                    />
                </Box>
            </Stack>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth required label="Account Holder Name" name="accountHolderName" value={formData.accountHolderName} onChange={handleInputChange} error={!!validationErrors.accountHolderName} helperText={validationErrors.accountHolderName} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth required label="Account Number" name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} error={!!validationErrors.accountNumber} helperText={validationErrors.accountNumber} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size='small' error={!!validationErrors.bankName}>
                        <InputLabel>Bank Name</InputLabel>
                        <Select label="Bank Name" name="bankName" value={formData.bankName} onChange={handleBankSelectChange} disabled={loading}>
                            <MenuItem value=""><em>Select Bank</em></MenuItem>
                            {mockBanks.map(bank => <MenuItem key={bank} value={bank}>{bank}</MenuItem>)}
                        </Select>
                        {validationErrors.bankName && <FormHelperText>{validationErrors.bankName}</FormHelperText>}
                    </FormControl>
                </Grid>
                {formData.bankName === 'Other' && (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField fullWidth required label="Other Bank Name" name="otherBankName" value={formData.otherBankName} onChange={handleInputChange} error={!!validationErrors.otherBankName} helperText={validationErrors.otherBankName} size='small' disabled={loading} />
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="Branch Name" name="branchName" value={formData.branchName} onChange={handleInputChange} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth size='small'>
                        <InputLabel>Account Type</InputLabel>
                        <Select label="Account Type" name="accountType" value={formData.accountType} onChange={handleInputChange} disabled={loading}>
                            {accountTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth required label="IFSC Code" name="ifscCode" value={formData.ifscCode} onChange={handleInputChange} error={!!validationErrors.ifscCode} helperText={validationErrors.ifscCode} size='small' disabled={loading} />
                </Grid>
            </Grid>

            <Typography variant='h6' fontWeight={600} mt={4} mb={2} sx={{ borderBottom: '1px solid #c3c3c3', pb: 1, fontSize: { xs: '1rem', md: '1.25rem' } }}>Statutory Compliance (India)</Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth required label="PAN Number" name="panNumber" value={formData.panNumber} onChange={handleInputChange} error={!!validationErrors.panNumber} helperText={validationErrors.panNumber} size='small' disabled={loading} inputProps={{ maxLength: 10 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="Aadhaar Number" name="aadhaarNumber" value={formData.aadhaarNumber} onChange={handleInputChange} error={!!validationErrors.aadhaarNumber} helperText={validationErrors.aadhaarNumber} size='small' disabled={loading} inputProps={{ maxLength: 12 }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="UAN" name="uanNumber" value={formData.uanNumber} onChange={handleInputChange} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="PF Number" name="pfNumber" value={formData.pfNumber} onChange={handleInputChange} error={!!validationErrors.pfNumber} helperText={validationErrors.pfNumber} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="PF Enrolment Date" name="pfStartDate" type="date" InputLabelProps={{ shrink: true }} value={formData.pfStartDate} onChange={handleInputChange} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="ESIC Number" name="esicNumber" value={formData.esicNumber} onChange={handleInputChange} error={!!validationErrors.esicNumber} helperText={validationErrors.esicNumber} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="ESI Enrolment Date" name="esiEnrolledDate" type="date" InputLabelProps={{ shrink: true }} value={formData.esiEnrolledDate} onChange={handleInputChange} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="Insurance Policy Number" name="insurancePolicyNumber" value={formData.insurancePolicyNumber} onChange={handleInputChange} error={!!validationErrors.insurancePolicyNumber} helperText={validationErrors.insurancePolicyNumber} size='small' disabled={loading} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField fullWidth label="Policy Issued Date" name="insurancePolicyIssuedDate" type="date" InputLabelProps={{ shrink: true }} value={formData.insurancePolicyIssuedDate} onChange={handleInputChange} size='small' disabled={loading} />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper elevation={2} sx={{ width: '100%', p: 2, bgcolor: 'info.light', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>Rehire Verification</Typography>
                            <Typography variant="caption">{navigationContext.stepLabel}</Typography>
                        </Box>
                        <Button variant="contained" color="info" onClick={handleSaveChanges} disabled={loading}>
                            {loading ? 'Saving...' : 'Verify & Return'}
                        </Button>
                    </Paper>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleSaveChanges} disabled={loading} sx={{ px: 4 }}>
                        {loading ? 'Saving...' : 'Save Details'}
                    </Button>
                )}
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default BankingStatutoryDetails;